<template>
  <transition name="_fade" mode="out-in" >
    <div class="pb-4" v-if="loaded">
      <vue-excel-xlsx
          v-if="!excelTopRows || !excelTopRows.length"
          ref="excel"
          class="d-none"
          :data="excelData"
          :columns="columnsForExcel"
          :filename="'report'"
          :sheetname="'report'">
      </vue-excel-xlsx>
      <div class="mb-3" v-if="hiddenColumns && hiddenColumns.length">
        <span class="text-muted mr-2">{{ $t('hidden_columns') }}:</span>
        <span v-for="c in hiddenColumns"
              @click="toggleColumn(c)"
              class="tags-note tag-code pointer mr-2"
              :key="`hidden-col-${c}`">{{ $t(c) }}
        </span>
      </div>
      <div class="mb-3 text-muted"
           v-if="!hideRowsCounter && sorted">
        {{ $t('records') }}: {{ formatNumber(sorted.length) }}
      </div>
      <div class="data-table-wrapper html-table-wrapper"
           ref="tableWrapper"
           :class="{ 'manycolumns': (columns.length - hiddenColumns.length) > 12 }">
        <table class="table data-table mb-0"
               ref="tableContentEl"
               :class="{ 'more-button': needsMoreButton }">
          <thead>
          <tr :class="`order-${sort}`">
            <template v-for="f in columns">
              <th @click="resort(f)"
                  :class="[
                  {
                    sortable: f.sortable,
                    sorted: order === (f.sortable && f.sortable !== true ? f.sortable : f.field),
                    primary: primaryKey == f.field,
                    hidden: hiddenColumns.includes(f.field)
                  },
               `th-${f.field}`]"
                  v-if="showTdInTable(f)"
                  class="data-table-th"
                  :key="`data-table-header-${f.field}`">
                <span v-if="!hiddenColumns.includes(f.field)">
                  {{ f.label }}
                  <span v-if="f.note"
                        v-b-tooltip.hover
                        class="px-2 mr-2"
                        :title="f.note">
                  <InfoSvg class="svg-size-md svg-light info-hover"/>
                </span>
                </span>
                <span v-if="canHideRows"
                      @click.stop="toggleColumn(f.field)"
                      class="toggle-column">
                  <EyeSvg v-b-tooltip.hover :title="f.label" v-if="hiddenColumns.includes(f.field)"/>
                  <NoEyeSvg v-if="!hiddenColumns.includes(f.field)"/>
                </span>
              </th>
            </template>
          </tr>
          </thead>
          <tbody>
          <tr class="html-table-search-tr" v-if="!hideWholeSearchPanel">
            <template v-for="f in columns">
              <th :key="`data-table-input-header-${f.field}`"
                  v-if="showTdInTable(f)"
                  :style="{ 'min-width': f.minWidth ? `${f.minWidth}px` : 'auto' }"
                  :class="{ hidden: hiddenColumns.includes(f.field) }">
                <template v-if="!hiddenColumns.includes(f.field)">
                  <select class="form-control sm mw-auto"
                          :disabled="f.search_select_disabled"
                          v-if="f.search_select || f.search_select_manual_options"
                          @change="selectChanged(f)"
                          v-model="searches[f.field]">
                    <option :value="null" v-if="!f.search_select_remove_null">-</option>
                    <option :value="isObject(o) ? o.id : o"
                            :key="`option-${f.field}-${oind}`"
                            v-for="(o, oind) in (f.search_select_manual_options ? f.search_select_manual_options : searchOptions[f.field])">
                      {{ f.format_translate ? $t(o) : (isObject(o) ? o.title : o) }}
                    </option>
                  </select>
                  <input v-else-if="!f.hide_search"
                         class="form-control sm mw-auto"
                         v-model="searches[f.field]"/>
                </template>
              </th>
            </template>
          </tr>
          <tr v-for="(l, lind) in sorted"
              :key="`data-table-${lind}`">
            <td v-if="l.wholerow"
                class="font-weight-bold pt-5"
                :colspan="columns.length">{{ l.wholerow }}</td>
            <template v-else
                      v-for="f in columns">
              <td :class="[{ hidden: hiddenColumns.includes(f.field) }, f.tabletdclass]"
                  v-if="(showTdInTable(f)) && l[f.field] !== '[tdskip]'"
                  :data-title="f.label"
                  :rowspan="f.rowspan ? f.rowspan : 1"
                  :style="{ 'min-width': f.minWidth ? `${f.minWidth}px` : 'auto' }"
                  :key="`data-table-${lind}-${f.field}`">
                <component :is="(f.tdcomponent && tdcomponents[f.tdcomponent]) ? tdcomponents[f.tdcomponent] : TDComponent"
                           v-if="!hiddenColumns.includes(f.field)"
                           :object="l"
                           :field="f"/>
              </td>
            </template>
            <MobileMoreArrow v-if="needsMoreButton"/>
          </tr>
          <tr v-if="(!sorted || sorted.length < 1) && !loading">
            <td :colspan="columns.length">{{ $t('no_data_available') }}</td>
          </tr>
          </tbody>
          <tfoot v-if="Object.keys(totals).length > 0">
          <tr>
            <template v-for="f in columns">
              <td v-if="showTdInTable(f)"
                  :data-title="f.label"
                  class="nowrap"
                  :class="{
                    hasData: !!totals[f.field],
                    hidden: hiddenColumns.includes(f.field)
                  }"
                  :key="`totals-${f.field}`">
                <template v-if="totals[f.field]">
                  <component v-if="f.totalscomponent && tdcomponents[f.totalscomponent]"
                             :is="tdcomponents[f.totalscomponent]"
                             :object="totals"
                             :field="f"/>
                  <div v-else-if="f.format_hours">{{ formatHours(totals[f.field]) }}</div>
                  <div v-else>{{ formatNumber(totals[f.field], true) }}</div>
                </template>
              </td>
            </template>
          </tr>
          <slot name="dt-footer"/>
          </tfoot>
        </table>
        <!--        <Paginator v-if="paginator" :paginator="paginator"/>-->
      </div>
      <StickyScrollbar :target="tableWrapper" :content-el="tableContentEl"/>
    </div>
  </transition>
</template>

<script setup>
import {ref, reactive, computed, watch, onMounted} from "vue"
import {useStore} from "vuex"
import {useRoute} from "vue-router"
import {LogService} from "@/services/api/log.api.service"
import MobileMoreArrow from "@/components/parts/datatable/MobileMoreArrow"
import TDComponent from "@/components/parts/datatable/TDComponent"
import EyeSvg from '@/assets/svg-vue/form/eye.svg'
import NoEyeSvg from '@/assets/svg-vue/form/noeye.svg'
import InfoSvg from '@/assets/svg-vue/general/question.svg'
import StickyScrollbar from "@/components/parts/general/StickyScrollbar"
import {formatHours, formatNumber} from "@/extensions/filters/filters"
import {copyObject, isObject} from "@/extensions/prototypes/prototypes"
import {dataTableHtmlTransformDateSearch} from "@/services/dataTable.service"
import XLSX from "xlsx/xlsx"

const props = defineProps({
  rows: {
    type: Array,
    required: true
  },
  columns: {
    type: Array,
    required: true
  },
  primaryKey: {
    type: String,
    default: 'id'
  },
  params: {
    type: Object,
  },
  initialOrder: {
    type: String,
    required: true
  },
  initialSort: {
    type: String,
    required: true
  },
  hideRowsCounter: {
    type: Boolean,
    default: false
  },
  hideWholeSearchPanel: {
    type: Boolean,
    default: false
  },
  canHideRows: {
    type: Boolean,
    default: true
  },
  transferToExcel: {
    type: Function,
    default: null
  },
  excelColumnsAdjustment: {
    type: Function,
    default: null
  },
  excelTopRows: {
    type: Array,
    default: null
  },
  excelTotals: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['loadReport'])

const store = useStore()
const route = useRoute()

const excel = ref(null)
const tableWrapper = ref(null)
const tableContentEl = ref(null)

const order = ref('id')
const sort = ref('asc')
const sorted = ref([])
const searches = reactive({})
const searchOptions = reactive({})
const totals = reactive({})
const loaded = ref(false)
const hiddenColumnsRaw = window.localStorage.getItem(route.name+'_hiddencolumns')
const hiddenColumns = ref(hiddenColumnsRaw ? JSON.parse(hiddenColumnsRaw) : [])

const rights = computed(() => store.state.auth.rights)
const loading = computed(() => store.state.dom.loading)
const counter = computed(() => store.state.datatable.dataTableCounter)

const needsMoreButton = computed(() => {
  if(!props.columns) return false
  return props.columns.length > 3
})

const tdcomponents = computed(() => {
  const components = require.context(`@/components/parts/datatable/td/`, true, /\.vue$/i)
  let arr = {}
  components.keys().map(x => {
    arr[x.split('/').pop().split('.')[0]] = components(x).default
  })
  return arr
})

const columnsForExcel = computed(() => {
  let cols = props.columns.map(col => {
    col.field = col.field ? col.field.toString() : ''
    return col
  }).filter(c => showTdInTable(c))
  if(props.excelColumnsAdjustment) {
    cols = props.excelColumnsAdjustment(cols)
  }
  return cols
})

const totalsRowForExcel = computed(() => {
  const row = {}
  columnsForExcel.value.forEach(col => {
    row[col.field] = totals[col.field] !== undefined ? totals[col.field] : ''
  })
  return row
})

const excelData = computed(() => {
  const data = props.transferToExcel ? props.transferToExcel(sorted.value) : sorted.value
  if(props.excelTotals && Object.keys(totals).length > 0) {
    const totalsRows = props.transferToExcel ? props.transferToExcel([totalsRowForExcel.value]) : [totalsRowForExcel.value]
    return [...data, {}, ...totalsRows]
  }
  return data
})

const tagsValuesToTotal = (htmlString, removeSubtotals = false) => {
  let res = 0
  const hasSubtotals = htmlString.includes('<b>')
  const regex = /<[^>]*>([^<]*)<\/[^>]*>/g
  let match
  while ((match = regex.exec(htmlString)) !== null) {
    const val = Number(match[1].trim().replace(/\s/g, ''))
    if(val) res += val
  }
  if(res > 0 && removeSubtotals && hasSubtotals) {
    res = res / 2
  }
  return res
}

const load = () => {
  sorted.value = copyObject(props.rows)
  for(let s in searches) {
    const column = props.columns.find(col => col.id === s)
    let q = searches[s]
    if(q !== null && q !== undefined && q !== '') {
      sorted.value = sorted.value.filter(function(element) {
        if(q.length > 1) {
          let firstTwoSymbols = q.substr(0, 2)
          if(firstTwoSymbols == '>=') {
            return parseInt(element[s]) >= parseInt(q.replace('>=', ''))
          }
          if(firstTwoSymbols == '<=') {
            return parseInt(element[s]) <= parseInt(q.replace('<=', ''))
          }
        }
        if(q[0] == '>') {
          return parseInt(element[s]) > parseInt(q.replace('>', ''))
        }
        if(q[0] == '<') {
          return parseInt(element[s]) < parseInt(q.replace('<', ''))
        }
        if(column.format_boolean && q === false && !element[s]) {
          return true
        }
        if(!element[s]) return false
        let string = element[s].toString()
        if(!string) return false
        if(column && column.search_select_strict) {
          return string.toLowerCase() == q.toLowerCase()
        }
        if(q === true || q === false) {
          return string.toLowerCase() === q.toString().toLowerCase()
        }
        if(column.format_datetime) {
          q = dataTableHtmlTransformDateSearch(q)
        }
        return string.toLowerCase().includes(q.toLowerCase())
      })
    }
  }
  if(props.params) {
    for(let param in props.params) {
      let condition = props.params[param]
      sorted.value = sorted.value.filter(function(element) {
        if(!element[param]) return false
        let string = element[param].toString()
        if(!string) return false
        return condition.includes(string)
      })
    }
  }
  props.columns.forEach(column => {
    if(column.totals) {
      totals[column.field] = sorted.value.reduce((a, b) => {
        let val = b[column.field]
        if(Array.isArray(val)) {
          val.forEach((item, index) => {
            let n = parseFloat(item)
            if(isNaN(n)) n = 0
            if(!Array.isArray(a)) a = [];
            a[index] = (a[index] ?? 0) + n
          })
          return a
        } else {
          if(val && typeof val === 'string' && val.includes('<')) {
            val = tagsValuesToTotal(val, column.removeSubtotals)
          }
          let n = parseFloat(val)
          if(isNaN(n)) { n = 0 }
          return a + n
        }
      }, 0)
    }
    if(column.totalsFn) {
      totals[column.field] = column.totalsFn(totals)
    }
  })
  if(!sorted.value) return
  if(!order.value) return
  if(sort.value === 'asc') {
    sorted.value.sort((a, b) => (a[order.value] > b[order.value]) ? 1 : -1)
  } else {
    sorted.value.sort((a, b) => (a[order.value] < b[order.value]) ? 1 : -1)
  }
}

const resort = (f) => {
  if(!f.sortable) return
  const sortable_field = f.sortable === true ? f.id : f.sortable
  if(order.value === sortable_field) {
    sort.value = sort.value === 'asc' ? 'desc': 'asc'
  } else {
    sort.value = 'asc'
  }
  order.value = sortable_field
  load()
}

const loadDictionaries = () => {
  order.value = props.initialOrder
  sort.value = props.initialSort
  props.columns.forEach(column => {
    if(column.search_select) {
      let arr = []
      if(column.search_doctor_one && props.rows) {
        props.rows.forEach(row => {
          let values = row[column.field].split(', ')
          arr = arr.concat(values)
        })
        arr = [...new Set(arr)]
      } else {
        arr = props.rows ? [...new Set(props.rows.map(row => row[column.field]))] : []
      }
      arr = arr.filter(n => n) // to remove empty strings
      searchOptions[column.field] = arr
    }
  })
}

const selectChanged = (field) => {
  if(field.changes_backend) {
    emit('loadReport', searches)
  }
}

const excelExport = () => {
  LogService.sendActivityLog({
    event: 'exported_report_to_excel',
    properties: { report: route.name }
  })
  if(props.excelTopRows && props.excelTopRows.length) {
    excelExportWithTopRows()
  } else {
    excel.value.$el.click()
  }
}

const getNestedValue = (object, string) => {
  string = string.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '')
  let a = string.split('.')
  for(let i = 0, n = a.length; i < n; ++i) {
    let k = a[i]
    if(k in object) {
      object = object[k]
    } else {
      return
    }
  }
  return object
}

const excelExportWithTopRows = () => {
  const headerRow = columnsForExcel.value.map(c => c.label)
  const dataRows = excelData.value.map(value => columnsForExcel.value.map(col => {
    let fieldValue = col.field.split('.').length > 1 ? getNestedValue(value, col.field) : value[col.field]
    return col.dataFormat && typeof col.dataFormat === 'function' ? col.dataFormat(fieldValue) : fieldValue
  }))
  const aoa = [...props.excelTopRows, headerRow, ...dataRows]
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  XLSX.utils.book_append_sheet(wb, ws, 'report')
  XLSX.writeFile(wb, 'report.xlsx')
}

const toggleColumn = (id) => {
  const index = hiddenColumns.value.indexOf(id)
  if (index > -1) {
    hiddenColumns.value.splice(index, 1)
  } else {
    hiddenColumns.value.push(id)
  }
  window.localStorage.setItem(route.name+'_hiddencolumns', JSON.stringify(hiddenColumns.value))
}

const showTdInTable = (field) => {
  if(field.right_includes && !rights.value.includes(field.right_includes)) {
    return false
  }
  if(field.right_excludes && rights.value.includes(field.right_excludes)) {
    return false
  }
  return true
}

watch(searches, () => load(), { deep: true })
watch(() => props.rows, () => {
  loadDictionaries()
  load()
}, { immediate: true })
watch(() => props.params, () => load(), { immediate: true })
watch(counter, () => load())

onMounted(() => {
  loaded.value = true
})

defineExpose({
  searches,
  excel: excelExport
})
</script>

<style scoped lang="scss">
.toggle-column {
  position: absolute;
  right: 5px;
  top: 15px;
  border-radius: 100%;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  //background: $label-color;
  background: $gray-icon;
  display: none;
  svg {

    width: 10px;
    height: 10px;
    fill: #fff;
  }
}
.data-table-th {
  &:hover, &.hidden {
    .toggle-column {
      display: inline-flex;
      svg {
        display: block;
      }
    }
  }
}
.info-hover {
  opacity: 0.7;
  transform: scale(0.9);
  transition: all 0.2s;
  &:hover {
    opacity: 1;
    transform: scale(1);
  }
}
th.hidden, td.hidden {
  width: 0;
  overflow: hidden;
  display: none;
  //font-size: 0;
}
.data-table {
  position: relative;
  thead {
    position: relative;
    th {
      position: sticky;
      top: 0;
    }
  }
}
.data-table-wrapper.html-table-wrapper {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
